import { useEffect, useState } from "react";
import { isMobile } from "react-device-detect";
import Modal from "react-modal";
import { useTranslation } from "react-i18next";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { colors } from "../../data/colors";
import { floatingPanelStyles } from "../../data/styles/modalStyles";
import { useChatModal } from "./useChatModal";
import ConversationList from "./ConversationList";
import ChatThread from "./ChatThread";
import NewChatPicker from "./NewChatPicker";

// Below this, the two-pane layout (260px list + 700px thread, plus the
// floating panel's own margins) can't actually fit the viewport, so the
// list pane is hidden in favor of a single full-width pane with a back
// button (ChatThread's own onBack) instead of letting both panes get
// squeezed illegibly narrow.
const NARROW_BREAKPOINT = 740;

const isNarrowLayout = () => isMobile || window.innerWidth < NARROW_BREAKPOINT;

export default function ChatModal({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const {
    me,
    conversations,
    loadingConversations,
    activeTarget,
    messages,
    loadingMessages,
    hasMoreMessages,
    composerText,
    setComposerText,
    sending,
    sendError,
    pickerOpen,
    setPickerOpen,
    openConversation,
    selectNewChatTarget,
    closeThread,
    loadOlderMessages,
    sendMessage,
    reactToMessage,
    removeReaction,
    editingMessageId,
    startEditMessage,
    cancelEdit,
    deleteMessage,
  } = useChatModal(open);

  const [isNarrow, setIsNarrow] = useState(isNarrowLayout);
  useEffect(() => {
    const handleResize = () => setIsNarrow(isNarrowLayout());
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const showList = !(isNarrow && activeTarget);
  const activeConversation = activeTarget
    ? conversations.find(
        (c) => c.type === activeTarget.type && c.code === activeTarget.code,
      )
    : undefined;

  const style = floatingPanelStyles({ z1: 80, z2: 81 });
  style.content = {
    ...style.content,
    width: activeTarget ? (isNarrow ? 380 : 700) : 380,
  };

  return (
    <Modal
      isOpen={open}
      onRequestClose={onClose}
      shouldCloseOnOverlayClick={false}
      style={style}
    >
      <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "8px 12px",
            backgroundColor: colors.darkGrey,
            color: colors.white,
          }}
        >
          <strong>{t("p.chat")}</strong>
          <CloseIcon
            className="noSelect"
            style={{ cursor: "pointer" }}
            onClick={onClose}
          />
        </div>
        <div style={{ flex: 1, display: "flex", minHeight: 0 }}>
          {pickerOpen ? (
            <div style={{ width: "100%", display: "flex", flexDirection: "column" }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "8px 12px",
                  backgroundColor: colors.chatPanelBg,
                }}
              >
                <ArrowBackIcon
                  className="noSelect"
                  style={{ cursor: "pointer" }}
                  onClick={() => setPickerOpen(false)}
                />
                <strong>{t("p.chatNewConversation")}</strong>
              </div>
              <div style={{ flex: 1, minHeight: 0 }}>
                <NewChatPicker onSelect={selectNewChatTarget} />
              </div>
            </div>
          ) : (
            <>
              {showList && (
                <div
                  style={{
                    width: activeTarget ? 260 : "100%",
                    flexShrink: 0,
                  }}
                >
                  <ConversationList
                    conversations={conversations}
                    loading={loadingConversations}
                    activeCode={activeTarget?.code}
                    activeType={activeTarget?.type}
                    onSelect={openConversation}
                    onNewChat={() => setPickerOpen(true)}
                  />
                </div>
              )}
              {activeTarget && (
                <div style={{ flex: 1, minWidth: 0 }}>
                  <ChatThread
                    me={me}
                    target={activeTarget}
                    online={!!activeConversation?.online}
                    lastActive={activeConversation?.lastActive ?? null}
                    messages={messages}
                    loading={loadingMessages}
                    hasMore={hasMoreMessages}
                    composerText={composerText}
                    setComposerText={setComposerText}
                    sending={sending}
                    sendError={sendError}
                    onBack={closeThread}
                    onLoadOlder={loadOlderMessages}
                    onSend={sendMessage}
                    onReact={reactToMessage}
                    onRemoveReaction={removeReaction}
                    editingMessageId={editingMessageId}
                    onStartEdit={startEditMessage}
                    onCancelEdit={cancelEdit}
                    onDelete={deleteMessage}
                  />
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </Modal>
  );
}
